import React, { useMemo, useState, useEffect } from 'react'
import {
    Page,
    View,
    Text as PDFText,
    Document,
    Image,
    StyleSheet,
} from '@react-pdf/renderer'

import { format } from 'date-fns'
import {
    ChatMessageFieldsFragment,
    MessageType,
    Attachment,
    AttachmentType,
} from '~graphql/generated/graphql'
import { getClientFullName } from '~utils/helpers'

// Create styles for consistent usage
const styles = StyleSheet?.create({
    page: {
        flexDirection: 'column',
        backgroundColor: '#FFFFFF',
        padding: 0,
        fontFamily: 'Helvetica',
    },
    // Redesigned header with more subtlety and professionalism
    header: {
        padding: '24px 40px 16px',
        borderBottom: '1px solid #E2E8F0',
        backgroundColor: '#FCFCFC',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    logoPlaceholder: {
        width: 24,
        height: 24,
        backgroundColor: '#3182CE',
        borderRadius: 12,
    },
    headerContent: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    headerAccent: {
        width: 4,
        height: 24,
        backgroundColor: '#3182CE',
        borderRadius: 2,
        marginRight: 8,
    },
    headerTextBlock: {
        flexDirection: 'column',
    },
    headerTitle: {
        fontSize: 14,
        fontWeight: 'bold',
        color: '#2D3748',
        marginBottom: 2,
    },
    headerSubtitle: {
        fontSize: 10,
        color: '#718096',
    },
    // More elegant footer with additional metadata
    footer: {
        position: 'absolute',
        bottom: 30,
        left: 40,
        right: 40,
        fontSize: 8,
        color: '#A0AEC0',
        textAlign: 'center',
        borderTop: '1px solid #EDF2F7',
        paddingTop: 10,
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    content: {
        padding: '24px 40px 40px',
    },
    // Improved date dividers
    sectionDivider: {
        flexDirection: 'row',
        alignItems: 'center',
        marginVertical: 12,
        width: '100%',
    },
    dividerLine: {
        flex: 1,
        height: 1,
        backgroundColor: '#EDF2F7',
    },
    dateContainer: {
        paddingHorizontal: 12,
    },
    dateText: {
        fontSize: 10,
        color: '#718096',
        fontWeight: 'bold',
    },
    // Message styling refinements
    messageContainer: {
        marginBottom: 16,
    },
    systemMessageContainer: {
        marginBottom: 16,
        paddingLeft: 0,
    },
    messageHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 6,
    },
    avatar: {
        width: 32, // Smaller avatar
        height: 32,
        borderRadius: 16,
        marginRight: 10,
        justifyContent: 'center',
        alignItems: 'center',
    },
    systemIndicator: {
        width: 3, // Thinner indicator
        height: 'auto',
        backgroundColor: '#3182CE',
        borderRadius: 2,
        marginRight: 10,
    },
    messageContent: {
        marginLeft: 42, // Align with the avatar + margin
    },
    systemMessageContent: {
        marginLeft: 13, // Align with system indicator + margin
        padding: '8px 12px',
        backgroundColor: '#F7FAFC', // Lighter blue background
        borderRadius: 4,
        borderLeft: '2px solid #3182CE', // Blue left border
    },
    nameText: {
        fontSize: 12,
        fontWeight: 'bold',
        color: '#2D3748',
    },
    systemInfoRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 4,
    },
    systemNameText: {
        fontSize: 11,
        fontWeight: 'bold',
        color: '#3182CE',
    },
    timestampText: {
        fontSize: 8,
        color: '#A0AEC0',
    },
    messageText: {
        fontSize: 10, // Smaller, more professional font size
        color: '#4A5568',
        lineHeight: 1.5,
    },
    systemMessageText: {
        fontSize: 10,
        color: '#2D3748',
        lineHeight: 1.5,
    },
    // Improved attachment styling
    attachmentContainer: {
        marginTop: 8,
        borderRadius: 4,
    },
    imageAttachment: {
        maxWidth: '100%',
        borderRadius: 3,
        border: '1px solid #E2E8F0',
        objectFit: 'contain',
    },
    attachmentInfo: {
        fontSize: 8,
        color: '#718096',
        marginTop: 4,
    },
    errorText: {
        fontSize: 8,
        color: '#E53E3E',
        marginTop: 3,
        fontStyle: 'italic',
    },
    // Message metadata
    metadataContainer: {
        marginTop: 4,
        flexDirection: 'row',
        alignItems: 'center',
    },
    metadataText: {
        fontSize: 8,
        color: '#A0AEC0',
        marginRight: 8,
    },
    metadataDot: {
        width: 3,
        height: 3,
        borderRadius: 1.5,
        backgroundColor: '#CBD5E0',
        marginRight: 8,
    },
    // Page counter styling
    pageNumber: {
        fontSize: 8,
        color: '#A0AEC0',
    },
})

export const MessagesPdfDoc: React.FC<{
    documentTitle: string
    messages: ChatMessageFieldsFragment[]
}> = ({ documentTitle, messages }) => {
    // Group messages by date for better organization
    const messagesByDate = useMemo(() => {
        const groups: Record<string, ChatMessageFieldsFragment[]> = {}

        messages.forEach(message => {
            const date = format(
                new Date(message.created_at),
                'EEEE, MMMM d, yyyy',
            )
            if (!groups[date]) {
                groups[date] = []
            }
            groups[date].push(message)
        })

        return groups
    }, [messages])

    // Get the current date for the footer
    const exportDate = format(new Date(), "MMM d, yyyy 'at' h:mm a")

    // Calculate total message count for header
    const totalMessages = messages.length

    // Calculate date range for conversations
    const dateRange = useMemo(() => {
        if (messages.length === 0) return 'No messages'

        const dates = messages.map(m => new Date(m.created_at))
        const oldestDate = new Date(Math.min(...dates.map(d => d.getTime())))
        const newestDate = new Date(Math.max(...dates.map(d => d.getTime())))

        // If same day, just show one date
        if (
            format(oldestDate, 'yyyy-MM-dd') ===
            format(newestDate, 'yyyy-MM-dd')
        ) {
            return format(oldestDate, 'MMM d, yyyy')
        }

        return `${format(oldestDate, 'MMM d')} - ${format(newestDate, 'MMM d, yyyy')}`
    }, [messages])

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                {/* Redesigned Header */}
                <View style={styles.header}>
                    <View style={styles.headerContent}>
                        <View style={styles.headerAccent} />
                        <View style={styles.headerTextBlock}>
                            <PDFText style={styles.headerTitle}>
                                {documentTitle}
                            </PDFText>
                            <PDFText style={styles.headerSubtitle}>
                                {`${totalMessages} messages · ${dateRange}`}
                            </PDFText>
                        </View>
                    </View>
                    <View style={styles.logoPlaceholder} />
                </View>

                {/* Content */}
                <View style={styles.content}>
                    {/* Iterate through message groups by date */}
                    {Object.entries(messagesByDate).map(([date, msgs]) => (
                        <View key={date}>
                            {/* Improved Date divider */}
                            <View style={styles.sectionDivider}>
                                <View style={styles.dividerLine} />
                                <View style={styles.dateContainer}>
                                    <PDFText style={styles.dateText}>
                                        {date}
                                    </PDFText>
                                </View>
                                <View style={styles.dividerLine} />
                            </View>

                            {/* Messages for this date */}
                            {msgs.map(msg => (
                                <PdfMessageItem key={msg.id} msg={msg} />
                            ))}
                        </View>
                    ))}
                </View>
            </Page>
        </Document>
    )
}

// Helper function to convert an image URL to a PNG Data URI
async function convertImageToPngDataUrl(imageUrl: string): Promise<string> {
    return new Promise((resolve, reject) => {
        const img = new window.Image()
        img.crossOrigin = 'Anonymous'
        img.onload = () => {
            const canvas = document.createElement('canvas')
            canvas.width = img.width
            canvas.height = img.height
            const ctx = canvas.getContext('2d')
            if (!ctx) {
                reject(
                    new Error('Could not get 2D rendering context from canvas'),
                )
                return
            }
            ctx.drawImage(img, 0, 0)
            try {
                const dataUrl = canvas.toDataURL('image/png')
                resolve(dataUrl)
            } catch (e) {
                reject(new Error(`Canvas toDataURL failed: ${e}`))
            }
        }
        img.onerror = errorEvent => {
            console.error('Image load error for URL:', imageUrl, errorEvent)
            reject(new Error(`Failed to load image from URL: ${imageUrl}`))
        }
        img.src = imageUrl
    })
}

type ProcessedAttachment = Attachment & {
    processedUrl: string
    conversionError?: boolean
    isProcessing?: boolean
}

function PdfMessageItem({ msg }: { msg: ChatMessageFieldsFragment }) {
    const userName = msg.clinic_user
        ? msg.clinic_user.first_name + ' ' + msg.clinic_user.last_name
        : null
    const clientName = msg.client ? getClientFullName(msg.client, true) : null

    const name = userName || clientName || 'Unknown'

    const isClinicUser = !!msg.clinic_user
    const isSystem = msg.message_type === MessageType.System

    const [processedAttachments, setProcessedAttachments] = useState<
        ProcessedAttachment[]
    >([])
    const [isLoadingAttachments, setIsLoadingAttachments] = useState(false)

    useEffect(() => {
        const processAllAttachments = async () => {
            if (!msg.attachments || msg.attachments.length === 0) {
                setProcessedAttachments([])
                setIsLoadingAttachments(false)
                return
            }

            setIsLoadingAttachments(true)
            // Initialize with isProcessing true for loading indicators
            setProcessedAttachments(
                msg.attachments.map(att => ({
                    ...att,
                    processedUrl: '', // Placeholder
                    isProcessing: true,
                })),
            )

            const results: ProcessedAttachment[] = []
            for (const attachment of msg.attachments) {
                let processedUrl = attachment.url
                let errorInConversion = false

                // Check if it's an image and not already a PNG
                if (
                    attachment.type === AttachmentType.Image &&
                    !attachment.url.toLowerCase().endsWith('.png')
                ) {
                    try {
                        processedUrl = await convertImageToPngDataUrl(
                            attachment.url,
                        )
                    } catch (err) {
                        console.error(
                            `Failed to convert ${attachment.filename} to PNG:`,
                            err,
                        )
                        errorInConversion = true
                        processedUrl = attachment.url // Fallback
                    }
                }

                results.push({
                    ...attachment,
                    processedUrl,
                    conversionError: errorInConversion,
                    isProcessing: false,
                })
            }
            setProcessedAttachments(results)
            setIsLoadingAttachments(false)
        }

        processAllAttachments()
    }, [msg.attachments])

    // Get user initials for avatar
    const initials = name
        .split(' ')
        .map(word => word[0])
        .filter(Boolean)
        .join('')
        .toUpperCase()
        .substring(0, 2)

    // Get avatar colors based on user type
    const avatarColor = isClinicUser
        ? '#4A5568' // Dark gray for clinic staff
        : '#3182CE' // Blue for clients

    // Format the message timestamp
    const timestamp = format(new Date(msg.created_at), 'h:mm a')

    // Additional metadata
    const messageType = isClinicUser ? 'Staff' : 'Client'
    const messageId = msg.id.substring(0, 8) // First 8 chars of ID as reference

    // Render message based on type (system or user)
    if (isSystem) {
        return (
            <View style={styles.systemMessageContainer} wrap={false}>
                <View style={styles.systemInfoRow}>
                    <PDFText style={styles.systemNameText}>System</PDFText>
                    <PDFText style={styles.timestampText}>{timestamp}</PDFText>
                </View>
                <View style={{ flexDirection: 'row' }}>
                    <View style={styles.systemIndicator} />
                    <View style={{ flex: 1 }}>
                        <View style={styles.systemMessageContent}>
                            {(msg.text || msg.email_text) && (
                                <PDFText style={styles.systemMessageText}>
                                    {msg.text || msg.email_text}
                                </PDFText>
                            )}

                            {/* Attachments */}
                            {renderAttachments()}

                            {/* Metadata row */}
                            <View style={styles.metadataContainer}>
                                <PDFText style={styles.metadataText}>
                                    System Message
                                </PDFText>
                                <View style={styles.metadataDot} />
                                <PDFText style={styles.metadataText}>
                                    ID: {messageId}
                                </PDFText>
                            </View>
                        </View>
                    </View>
                </View>
            </View>
        )
    }

    // Standard user message
    return (
        <View style={styles.messageContainer} wrap={false}>
            <View style={styles.messageHeader}>
                {/* User Avatar */}
                <View style={[styles.avatar, { backgroundColor: avatarColor }]}>
                    <PDFText
                        style={{
                            color: 'white',
                            fontSize: 11,
                            fontWeight: 'bold',
                        }}
                    >
                        {initials}
                    </PDFText>
                </View>

                {/* User Name & Timestamp */}
                <View style={{ flex: 1 }}>
                    <PDFText style={styles.nameText}>{name}</PDFText>
                    <PDFText style={styles.timestampText}>{timestamp}</PDFText>
                </View>
            </View>

            {/* Message Content */}
            <View style={styles.messageContent}>
                {(msg.text || msg.email_text) && (
                    <PDFText style={styles.messageText}>
                        {msg.text || msg.email_text}
                    </PDFText>
                )}

                {/* Attachments */}
                {renderAttachments()}

                {/* Metadata row */}
                <View style={styles.metadataContainer}>
                    <PDFText style={styles.metadataText}>{messageType}</PDFText>
                    <View style={styles.metadataDot} />
                    <PDFText style={styles.metadataText}>
                        ID: {messageId}
                    </PDFText>
                </View>
            </View>
        </View>
    )

    // Helper function to render attachments
    function renderAttachments() {
        if (isLoadingAttachments && processedAttachments.length === 0) {
            return (
                <PDFText style={styles.attachmentInfo}>
                    Loading attachments...
                </PDFText>
            )
        }

        return processedAttachments.map(attachment => (
            <View
                key={attachment.id}
                style={styles.attachmentContainer}
                wrap={false}
            >
                {attachment.isProcessing ? (
                    <PDFText style={styles.attachmentInfo}>
                        Processing attachment: {attachment.filename}...
                    </PDFText>
                ) : attachment.type === AttachmentType.Image ? (
                    <>
                        <View style={{ maxHeight: '300px' }}>
                            <Image
                                style={styles.imageAttachment}
                                src={attachment.processedUrl}
                            />
                        </View>
                        <PDFText style={styles.attachmentInfo}>
                            {attachment.filename}
                        </PDFText>
                    </>
                ) : (
                    <PDFText style={styles.attachmentInfo}>
                        Attachment: {attachment.filename} (Type:{' '}
                        {attachment.type})
                    </PDFText>
                )}

                {attachment.conversionError && (
                    <PDFText style={styles.errorText}>
                        (Image conversion failed. Original format shown if
                        possible.)
                    </PDFText>
                )}
            </View>
        ))
    }
}
